<template>
  <div class="configuration-form">
    <h4 class="title">Configuration</h4>
    <p class="subtitle">Please configure your gitlab pipeline dashboard.</p>
    <b-card class="text-dark">
      <b-form v-on:submit.prevent="onSubmit" ref="form">
        <div role="group">
          <label for="input-url">GitLab URL: <span class="text-danger">*</span></label>
          <b-form-input
              id="input-url"
              ref="input-url"
              v-model="formData.url"
              required
              :state="validateInput('input-url')"
              trim
              type="url"
              placeholder=""
              aria-describedby="input-url-feedback"
              :aria-invalid="validateInput('input-url')"
          />
          <b-form-feedback class="feedback" id="input-url-feedback">
            Please enter a valid URL.
          </b-form-feedback>
<!--          TODO check validation -->
        </div>

        <div role="group">
          <label for="input-url">
            API Access Token: <span class="text-danger">*</span><br/>
            <span class="hint">Retrieve 'read_api' token from <a
                href="test"
                xxhref="$data.formData.url+'/-/user_settings/personal_access_tokens'">/-/user_settings/personal_access_tokens</a></span>
          </label>
          <b-form-input
              id="input-token"
              ref="input-token"
              v-model="formData.token"
              required
              :state="validateInput('input-token')"
              trim
              autocomplete="none"
              type="password"
              aria-describedby="input-token-feedback"
              :aria-invalid="validateInput('input-token')"
          />
          <BFormValidFeedback class="feedback" id="input-token-feedback">
            Enter a API access token
          </BFormValidFeedback>
        </div>

        <div role="group">
          <label for="input-url">Groups:
            <span class="hint">separated by semicolon: ';'</span></label>
          <b-form-input
              id="input-group"
              ref="input-group"
              v-model="formData.groups"
              :required="!formData.groups && !formData.projects"
              trim
              type="text"
              placeholder="supers; winners"
              aria-describedby="input-group-text"
          />
          <b-form-text class="feedback badges" id="input-group-text">
            <b-badge
                variant="primary"
                pill
                v-for="(group, key) in splitNames(formData.groups)"
                :key="key"
            >
              {{ group }}
            </b-badge>
          </b-form-text>
        </div>

        <div role="group">
          <label for="input-url">Projects: <br/>
            <span class="hint">exact path with namespace: e.g. lkwg82/gitlab-pipeline-dashboard, separated by semicolon: ';'</span>
          </label>
          <b-form-input
              id="input-projects"
              ref="input-projects"
              v-model="formData.projects"
              trim
              type="text"
              placeholder="alpha; beta"
              aria-describedby="input-projects-text"
          />
          <b-form-text class="feedback badges" id="input-projects-text">
            <b-badge
                variant="info"
                pill
                v-for="(project, key) in splitNames(formData.projects)"
                :key="key"
            >
              {{ project }}
            </b-badge>
          </b-form-text>
        </div>

        <div role="group">
          <label for="group-groups-checkbox">group by groups </label>
          <b-form-checkbox
              id="group-groups-checkbox"
              ref="group-groups"
              @change="onSelectGroupingByGroups"
              v-model="formData.group_by_groups"
              :value=true
              :unchecked-value=false
              aria-describedby="input-group-groups-checkbox"
          >group projects by group vertically
          </b-form-checkbox>
        </div>
        <div role="group">
          <label for="group-namespaces-checkbox">group by namespaces </label>
          <b-form-checkbox
              id="group-namespaces-checkbox"
              ref="group-namespaces"
              v-model="formData.group_by_namespaces"
              @change="onSelectGroupingByNamespace"
              :value=true
              :unchecked-value=false
              aria-describedby="input-group-namespaces-checkbox"
          >group projects by namespace vertically
          </b-form-checkbox>
        </div>
        <div role="group">
          <label for="include-shared-projects-checkbox">include shared projects</label>
          <b-form-checkbox
              id="include-shared-projects-checkbox"
              ref="include-shared-projects"
              v-model="formData.include_shared_projects"
              :value=true
              :unchecked-value=false
              aria-describedby="input-shared-projects-checkbox"
          >include shared projects
          </b-form-checkbox>
        </div>
        <div class="experiments">
          <hr/>
          🏗 Experimente <span class="s1">😁</span>
          <hr/>
        </div>
        <div role="group">
          <label for="order-by-failures-checkbox">order by failures <sup>BETA</sup></label>
          <b-form-checkbox
              id="order-by-failures-checkbox"
              ref="order-by-failures"
              v-model="formData.beta_order_by_failures_first"
              aria-describedby="input-order-by-failures-checkbox"
          >order by failures (failures first)
          </b-form-checkbox>
        </div>
        <div role="group">
          <label for="protected-branches-checkbox">show only protected branches <sup>BETA</sup></label>
          <b-form-checkbox
              id="protected-branches-checkbox"
              ref="show-only-protected-branches"
              v-model="formData.beta_show_only_protected_branches"
              aria-describedby="protected-branches-checkbox"
          >show last pipeline status per protected branch instead of status of last pipeline for project
          </b-form-checkbox>
        </div>
        <div role="group">
          <label for="show-schedules-builds-checkbox">show status of schedules builds<sup>BETA</sup></label>
          <b-form-checkbox
              id="show-schedules-builds-checkbox"
              ref="show-schedules-builds"
              v-model="formData.beta_show_scheduled_builds"
              aria-describedby="show-schedules-builds-checkbox"
          >shows status of last pipeline from active schedules
          </b-form-checkbox>
        </div>
        <div role="group" v-if="show_snow_fun_config()">
          <label for="show-snow-checkbox">show snow because ... ?<sup>ALPHA</sup></label>
          <b-form-checkbox
              id="show-snow-checkbox"
              ref="show-snow"
              v-model="formData.alpha_show_snow"
              aria-describedby="show-snow-checkbox"
          >let it snow
          </b-form-checkbox>
        </div>

        <div role="group">
            <label for="show-history-checkbox">show pipeline history<sup>ALPHA</sup></label>
            <b-form-checkbox
                    id="show-history-checkbox"
                    ref="show-history"
                    v-model="formData.beta_show_history"
                    aria-describedby="show-history-checkbox"
            >shows status of the last 20 pipelines when job is failed
            </b-form-checkbox>
        </div>
        <div role="group">
            <label for="no-polling-checkbox">deactivate polling pipeline updates (needs browser refresh)<sup>BETA</sup></label>
            <b-form-checkbox
                    id="no-polling-checkbox"
                    ref="no-polling"
                    v-model="formData.beta_no_polling"
                    aria-describedby="no-polling-checkbox"
            >avoids periodic updates in background (each updates needs a page refresh)
            </b-form-checkbox>
        </div>

        <!-- 🔗 Share-Link für Config -->
        <div role="group" class="share-section" v-if="shareUrl">
          <hr />
          <label>📤 Share Config URL:</label>
          <div class="input-group">
            <input
              type="text"
              class="form-control"
              :value="shareUrl"
              readonly
              @click="$event.target.select()"
              placeholder="Generated share URL will appear here..."
            />
            <button
              type="button"
              class="btn"
              :class="
                copyButtonState === 'copied'
                  ? 'btn-success'
                  : 'btn-outline-secondary'
              "
              @click="copyShareUrl"
              :disabled="!shareUrl"
            >
              {{ copyButtonState === "copied" ? "✅ Copied!" : "📋 Copy" }}
            </button>
          </div>
          <small class="text-muted">
            Share this URL to let others use your group/project configuration
          </small>
        </div>

        <b-button
            type="submit"
            variant="primary"
            xvariant="checkValidity() ? 'primary' : 'outline-primary'"
            @click="onSubmit">
          Save
        </b-button>

      </b-form>
    </b-card>
  </div>
</template>

<script lang="ts">
import  {defineComponent} from 'vue'
import {Config} from '@/interfaces'
import is_christmas_snowtime from "@/clients/fun_show";
import { BCard, BForm, BFormInput, BFormCheckbox, BFormText, BBadge, BButton } from 'bootstrap-vue-next';
export default defineComponent({
  name: 'ConfigurationForm',
  components: {
    BCard,
    BForm,
    BFormInput,
    BFormCheckbox,
    BFormText,
    BBadge,
    BButton,
  },
  props: {
    config: {
      type: Object as () => Config,
      required: true,
    },
  },
  data: () => ({
    submitted: false,
    formData: {
      url: '',
      token: '',
      groups: '',
      projects: '',
      group_by_groups: true,
      group_by_namespaces: false,
      include_shared_projects: false,
      beta_show_only_protected_branches: false,
      beta_order_by_failures_first: false,
      beta_show_scheduled_builds: true,
      beta_show_history: true,
      beta_no_polling: false
    } as Config,
    copyButtonState: "copy" as "copy" | "copied", // 📋 Button state tracking
  }),
  computed: {
    // 🔗 Generate share URL with group, project and gitlab URL config
    shareUrl(): string {
      if (
        !this.formData.groups &&
        !this.formData.projects &&
        !this.formData.url
      ) {
        return "";
      }

      const baseUrl = window.location.origin + window.location.pathname;
      const params = new URLSearchParams();

      // 🌐 GitLab URL (nur wenn nicht Standard)
      if (
        this.formData.url?.trim() &&
        this.formData.url !== "https://gitlab.com"
      ) {
        params.set("gitlab_url", this.formData.url);
      }

      if (this.formData.groups?.trim()) {
        params.set("add_groups", this.formData.groups);
      }

      if (this.formData.projects?.trim()) {
        params.set("add_projects", this.formData.projects);
      }

      return params.toString() ? `${baseUrl}?${params.toString()}` : "";
    },
  },
  watch: {
    config: function (config) {
      this.formData = {...config}
    },
  },
  methods: {
    show_snow_fun_config:is_christmas_snowtime,
    checkValidity() {
      const form = this.$refs.form as HTMLFormElement
      if (!this.formData.groups && !this.formData.projects) {
        return false
      }
      if (form) {
        return form.checkValidity()
      }
      return false
    },
    onSubmit() {
      this.submitted = true
      console.log(this.formData)
      this.$emit('onSubmit', this.formData)
    },
    validateInput(ref: string) {
      if (!this.submitted) {
        return null
      }
      const input = this.$refs[ref] as HTMLInputElement
      return (input && (input.value || this.submitted)) ? input.validity.valid && input.value : null
    },
    onSelectGroupingByGroups(){
      if (this.formData.group_by_groups){
        this.formData.group_by_namespaces = false
      }
    },
    onSelectGroupingByNamespace(){
      if (this.formData.group_by_namespaces){
        this.formData.group_by_groups = false
      }
    },
    splitNames(names: string) {
      return names.split(';')
          .map((part) => part.trim())
          .filter((part) => part !== '')
    },
    // 📋 Copy share URL to clipboard
    async copyShareUrl() {
      try {
        await navigator.clipboard.writeText(this.shareUrl);
        this.copyButtonState = "copied"; // ✅ Success state
        console.log("Share URL copied to clipboard!");

        // Reset back to 'copy' after 2 seconds
        setTimeout(() => {
          this.copyButtonState = "copy";
        }, 2000);
      } catch (err) {
        console.error("Failed to copy URL:", err);
        // Fallback für ältere Browser
        const textArea = document.createElement("textarea");
        textArea.value = this.shareUrl;
        document.body.appendChild(textArea);
        textArea.select();
        document.execCommand("copy");
        document.body.removeChild(textArea);

        this.copyButtonState = "copied"; // ✅ Success state auch für fallback
        setTimeout(() => {
          this.copyButtonState = "copy";
        }, 2000);
      }
    },
  },
  created() {
    this.formData = {...this.config}
  },
})
</script>

<style scoped lang="scss">

.subtitle {
  line-height: 1;
}

div[role="group"] {
  margin-bottom: 0.5rem;
}

label {
  margin-bottom: 0.1rem;

  .hint {
    font-size: small;

    a {
      color: blue
    }
  }
}

input {
  display: inline-block;

  &::placeholder {
    color: #cacaca;
  }
}

.feedback {
  margin-bottom: 0;
}

.badges {
  font-size: 1rem;

  .badge {
    margin-right: 0.2rem;
  }
}

button {
  float: right;
  margin-top: 1rem;
  min-width: 6rem;
}

/* 🔗 Share section styling */
.share-section {
  margin-top: 1rem;

  .input-group {
    margin-bottom: 0.5rem;

    input {
      background-color: #f8f9fa;
      cursor: pointer;
    }

    button {
      float: none;
      margin: 0;
      min-width: auto;
    }
  }

  small {
    display: block;
    margin-top: 0.25rem;
  }
}

.experiments {
  animation-name: running;
  animation-duration: 2s;
  animation-timing-function: ease-in-out;
  animation-direction: alternate;
  animation-iteration-count: infinite;
  animation-play-state: running;

  @keyframes running {
    0% {
      font-size: large;

      opacity: 0;
    }

    50% {
      font-size: larger;

    }

    100% {

      font-size: xx-large;
      opacity: 1;
    }
  }
}

</style>
